import { createRoot } from 'react-dom/client'
import App from './App'
import './index.css'
import './hero-scroll-takeover.css'
import { inject } from '@vercel/analytics'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { getSupabaseEnvStatus } from '@/integrations/supabase/client'
import { logSecurityConfig, getSecurityConfig } from '@/config/security'
import '@/lib/viewport-fix'
import { initSentry } from '@/lib/sentry'
import { PostHogProvider } from '@posthog/react'
import { shouldExcludeAnalytics } from '@/lib/posthog'
import { initMetaPixel } from '@/lib/meta-pixel'

// Initialize security configuration
const securityConfig = getSecurityConfig();

// Initialize Sentry (only in production and with error reporting enabled)
if (securityConfig.enableErrorReporting && securityConfig.isProduction) {
  initSentry();
}

// Initialize analytics (only if enabled)
if (securityConfig.enableAnalytics) {
  inject(); // Vercel Analytics
  initMetaPixel(); // Meta Pixel
}

// Log security and environment status
logSecurityConfig();
console.log('ENV_STATUS', getSupabaseEnvStatus())

const posthogKey = import.meta.env.VITE_POSTHOG_API_KEY;
const posthogHost = import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com';
const environment = import.meta.env.VITE_POSTHOG_ENVIRONMENT || 'production';

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary>
    {securityConfig.enableAnalytics && posthogKey ? (
      <PostHogProvider
        apiKey={posthogKey}
        options={{
          api_host: posthogHost,

          // Environment configuration
          disable_session_recording: environment === 'development',
          disable_surveys: environment === 'development',

          // Privacy and compliance
          respect_dnt: true,
          capture_pageview: true,
          capture_pageleave: 'if_capture_pageview',

          // Session recording privacy
          session_recording: {
            maskAllInputs: true,
            recordCrossOriginIframes: false,
          },

          // Autocapture configuration
          autocapture: true,

          // Drop internal browsers, preview deployments, bots, and browser automation
          // before events leave the device. The legacy signup event is emitted at
          // authentication time, so normalize it here without risking the auth flow.
          before_send: (event) => {
            if (shouldExcludeAnalytics()) return null;

            if (event.event === 'signup_completed') {
              return {
                ...event,
                event: 'authentication_completed',
                properties: {
                  ...event.properties,
                  legacy_event_name: 'signup_completed',
                },
              };
            }

            return event;
          },
        }}
      >
        <App />
      </PostHogProvider>
    ) : (
      <App />
    )}
  </ErrorBoundary>
);
